import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Col, Row, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_organizations, get_workspaces, get_modules, set_org_filters, get_cycles } from '../../redux/actions/organizationActions';

const OrgFilters = (props) => {
	const { orgFilters, setOrgFilters, displayFilters, disableOrganization = false } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { user, isAdminUser } = useSelector(state => state.auth);

	const { organizations, workspaces, modules, cycles } = useSelector(state => state.organization);

	// Filter workspaces based on user type
	const filteredWorkspaces = isAdminUser
		? workspaces
		: workspaces?.filter(workspace => workspace.value === 2) || [];

	useEffect(() => {
		if (!disableOrganization) {
			dispatch(get_organizations());
		}
	}, [disableOrganization]);

	useEffect(() => {
		let _organization;
		
		if (disableOrganization) {
			// For disabled organization (client users), use their organization from user object
			_organization = user.organization || '';
		} else {
			// For admin users, use the existing logic
			_organization = (organizations.length <= 1) 
				? user.organization 
				: localStorage.getItem('organization') || '';
		}

		const _orgFilters = {
			...orgFilters,
			organization: _organization,
			workspace: localStorage.getItem('workspace') || '',
			module: localStorage.getItem('module') || '',
			cycle: localStorage.getItem('cycle') || ''
		}
		
		setOrgFilters(_orgFilters);
		dispatch(set_org_filters(_orgFilters));
	}, [organizations, disableOrganization, user]);

	useEffect(() => {
		// setDisplayData(false);
		if (orgFilters.organization !== '') {
			dispatch(get_workspaces({ organization: orgFilters.organization }));
			dispatch(get_modules({ organization: orgFilters.organization }));
		}
		if (orgFilters.module !== '') {
			dispatch(get_cycles({ workspace: orgFilters.workspace, module: orgFilters.module }));
		}
	}, [orgFilters]);

	const onChangeOrgFilters = (e, name) => {
		const newFilters = { ...orgFilters };
		const keys = Object.keys(newFilters);
		const idx = keys.indexOf(name);

		// Reset all filters from the current index onward
		keys.slice(idx).forEach((key) => {
			newFilters[key] = '';
			localStorage.setItem(key, '');
		});

		// Set the new value if e is provided
		if (e?.value !== undefined) {
			newFilters[name] = e.value;
			localStorage.setItem(name, e.value);
		}

		setOrgFilters(newFilters);
		dispatch(set_org_filters(newFilters));
	};

	return (
		<Row justify='center' gutter={[16, 16]}>
			{displayFilters?.organization &&
				organizations.length > 1 &&
					<Col className='gutter-row' style={{ zIndex: 10 }} lg={6} md={8} sm={24} xs={24}>
						<Row justify='center'>
							<Typography.Text><i className='bi bi-house me-2'></i>{t('filters.organization')}</Typography.Text>
						</Row>
						<Select
							style={{ width: '100%' }}
							listHeight={160}
							options={disableOrganization ? 
								[{ value: user.organization, label: localStorage.getItem('organization_name') || 'Mi Organización' }] :
								organizations.map(option => ({ value: option._id.$oid, label: option.name }))
							}
							value={disableOrganization ? 
								localStorage.getItem('organization_name') || 'Mi Organización' :
								organizations.find(option => option._id.$oid === orgFilters.organization)?.name
							}
							onChange={(value, option) => onChangeOrgFilters(option, 'organization')}
							disabled={disableOrganization}
							allowClear={!disableOrganization}
							showSearch={!disableOrganization}
							filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						/>
					</Col>
				
			}
			{displayFilters?.workspace &&
				<Col className='gutter-row' style={{ zIndex: 10 }} lg={6} md={8} sm={24} xs={24}>
					<Row justify='center'>
						<Typography.Text><i className='bi bi-list me-2'></i>{t('filters.workspace')}</Typography.Text>
					</Row>
					<Select
						style={{ width: '100%' }}
						listHeight={160}
						options={filteredWorkspaces.map(option => ({ value: option._id.$oid, label: option.name }))}
						value={filteredWorkspaces.find(option => option._id.$oid === orgFilters.workspace)?.name}
						onChange={(value, option) => onChangeOrgFilters(option, 'workspace')}
						disabled={false}
						allowClear={true}
						showSearch
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
					/>
				</Col>
			}
			{displayFilters?.module &&
				<Col className='gutter-row' style={{ zIndex: 10 }} lg={6} md={8} sm={24} xs={24}>
					<Row justify='center'>
						<Typography.Text><i className='bi bi-box2 me-2'></i>{t('filters.module')}</Typography.Text>
					</Row>
					<Select
						style={{ width: '100%' }}
						listHeight={160}
						options={modules.map(option => ({ value: option._id.$oid, label: option.name }))}
						value={modules.find(option => option._id.$oid === orgFilters.module)?.name}
						onChange={(value, option) => onChangeOrgFilters(option, 'module')}
						disabled={false}
						allowClear={true}
						showSearch
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
					/>
				</Col>
			}
			{displayFilters?.cycle &&
				<Col className='gutter-row' style={{ zIndex: 10 }} lg={6} md={8} sm={24} xs={24}>
					<Row justify='center'>
						<Typography.Text><i className='bi bi-arrow-repeat me-2'></i>{t('filters.cycle')}</Typography.Text>
					</Row>
					<Select
						style={{ width: '100%' }}
						listHeight={160}
						options={cycles.map(option => ({ value: option._id.$oid, label: option.name }))}
						value={cycles.find(option => option._id.$oid === orgFilters.cycle)?.name}
						onChange={(value, option) => onChangeOrgFilters(option, 'cycle')}
						disabled={false}
						allowClear={true}
						showSearch
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
					/>
				</Col>
			}
		</Row>
	);
}

OrgFilters.propTypes = {
	orgFilters: PropTypes.object.isRequired,
	setOrgFilters: PropTypes.func.isRequired,
	displayFilters: PropTypes.object.isRequired,
	disableOrganization: PropTypes.bool
};

export default OrgFilters;